import { useMemo, useState } from "react";
import { CanvasRow, CanvasRows } from "../@types";
import { ComponentsWidth, components } from "../consts";
import {
  findRowByComponentId,
  insert,
  insertIntoRows,
  removeFromRowById,
  swapItems
} from "../utils";

const { FULL } = ComponentsWidth;

export type GridRowsReturn = {
  canvasRows: CanvasRows;
  api: {
    clear: () => void;
    insertToEmptyRow: (rows: CanvasRows, id: string, row?: number) => void;
    insertToRows: (rows: CanvasRows, id: string) => void;
    insertToRowById: (rows: CanvasRows, id: string, rowId: number) => void;
    removeById: (rows: CanvasRows, id: string) => void;
    replaceById: (rows: CanvasRows, id: string, overId: string) => void;
    setRows: (rows: CanvasRows) => void;
  };
};

const useGridRows = (): GridRowsReturn => {
  const [canvasRows, setCanvasRows] = useState<CanvasRows>([]);

  const api = useMemo(() => {
    const setRows = (rows: CanvasRows) => {
      setCanvasRows(rows.filter(r => r.components.length));
    };

    const swapByIds = (cRows: CanvasRows, id: string, overId: string) => {
      const rows = swapItems(cRows, id, overId);
      setRows(rows);
    };

    /**
     * Removes item by id
     */
    const removeById = (cRows: CanvasRows, id: string) => {
      const { index } = findRowByComponentId(cRows, id);
      if (index === null) return;

      const rows = removeFromRowById(cRows, index, id);
      setRows(rows);
    };

    /**
     * Inserts item into a row by row id
     */
    const insertToRowById = (cRows: CanvasRows, id: string, rowId: number) => {
      let rows = [...cRows];
      const row = rows[rowId];

      const { index: containedIndex } = findRowByComponentId(rows, id);

      if (containedIndex !== null) {
        rows = removeFromRowById(rows, containedIndex, id);
      }

      const { width } = components[id];

      if (width === FULL || row?.width === FULL) {
        const component = { components: [id], width };
        setRows(insert<CanvasRow>(rows, component, rowId));
      } else {
        row.components.push(id);
        row.width = FULL;

        setRows(rows);
      }
    };

    /**
     * Inserts item into rows
     */
    const insertToRows = (cRows: CanvasRows, id: string) => {
      const rows = insertIntoRows(cRows, id);
      setRows(rows);
    };

    /**
     * Inserts item into empty to row by id
     */
    const insertToEmptyRow = (cRows: CanvasRows, id: string, row?: number) => {
      let rows = [...cRows];
      const { index: containedIndex } = findRowByComponentId(rows, id);

      if (containedIndex !== null) {
        rows = removeFromRowById(rows, containedIndex, id);
      }

      const { width } = components[id];
      const component = { components: [id], width };

      setRows(insert<CanvasRow>(rows, component, row ?? cRows.length));
    };

    /**
     * Replaces one item with another by id
     * When items are the same width, it swaps their places otherwise it pushes
     * one above the other
     */
    const replaceById = (cRows: CanvasRows, id: string, overId: string) => {
      if (id === overId) return;
      let rows = [...cRows];

      const { index: overIndex } = findRowByComponentId(rows, overId);

      if (overIndex === null) return;

      const { index: containedIndex } = findRowByComponentId(rows, id);

      const overComponent = components[overId];
      const { width } = components[id];

      if (width === overComponent.width) {
        swapByIds(cRows, id, overId);
        return;
      }

      if (containedIndex !== null) {
        rows = removeFromRowById(rows, containedIndex, id);
      }

      const component = { components: [id], width };
      setRows(insert<CanvasRow>(rows, component, overIndex));
    };

    const clear = () => {
      setRows([]);
    };

    return {
      clear,
      insertToEmptyRow,
      insertToRows,
      insertToRowById,
      removeById,
      replaceById,
      setRows
    };
  }, []);

  return {
    canvasRows,
    api
  };
};

export default useGridRows;
